import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Clock, LogIn, LogOut, X } from 'lucide-react';
import api from '../api/client';
import Dialog from './Dialog';
import { derivePunchClockUiState } from '../utils/timeclockState';

interface Props {
  onClose: () => void;
}

export default function PunchClockModal({ onClose }: Props) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const { data: status, isLoading, error } = useQuery<{
    punched_in: boolean;
    active_since: string | null;
    kimai_user_id: number | null;
  }>({
    queryKey: ['timeclock-status'],
    queryFn: () => api.get('/timeclock/status').then(r => r.data),
    meta: { expectedStatuses: [404] },
    retry: false,
  });

  // Elapsed timer
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!status?.punched_in || !status.active_since) return;
    const start = new Date(status.active_since).getTime();
    const tick = () => {
      const diff = Math.floor((Date.now() - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [status?.punched_in, status?.active_since]);

  const punchIn = useMutation({
    mutationFn: () => api.post('/timeclock/punch-in').then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeclock-status'] });
      queryClient.invalidateQueries({ queryKey: ['timeclock-timesheet'] });
      setFeedback({ type: 'success', msg: 'Punched in!' });
      setTimeout(() => setFeedback(null), 3000);
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed';
      setFeedback({ type: 'error', msg: detail });
    },
  });

  const punchOut = useMutation({
    mutationFn: () => api.post('/timeclock/punch-out').then(r => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timeclock-status'] });
      queryClient.invalidateQueries({ queryKey: ['timeclock-timesheet'] });
      setFeedback({ type: 'success', msg: `Punched out — ${data.hours}h logged` });
      setTimeout(() => setFeedback(null), 4000);
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed';
      setFeedback({ type: 'error', msg: detail });
    },
  });

  const punchUiState = derivePunchClockUiState(status, isLoading, error);

  return (
    <Dialog
      onClose={onClose}
      ariaLabel="Punch clock"
      overlayStyle={{ background: 'rgba(0,0,0,0.4)' }}
      contentStyle={{
        background: 'var(--card)', borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border)',
        width: 'min(360px, 92vw)', padding: '1.5rem',
      }}
    >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={20} style={{ color: 'var(--primary)' }} />
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Punch Clock</h3>
          </div>
          <button onClick={onClose} aria-label="Close punch clock" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={18} />
          </button>
        </div>

        {punchUiState.kind === 'loading' && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading...</div>
        )}

        {punchUiState.kind === 'not-configured' && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {punchUiState.reason === 'firmwide-off'
              ? 'Time clock is not configured for the firm. Contact an administrator.'
              : 'Time clock not configured for your account. Contact an administrator.'}
          </div>
        )}

        {(punchUiState.kind === 'ready' || punchUiState.kind === 'recoverable-error') && (
          <div style={{ textAlign: 'center' }}>
            {punchUiState.kind === 'recoverable-error' && (
              <div role="alert" style={{
                padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)', marginBottom: '0.75rem',
                background: '#fef3c7', color: '#92400e', border: '1px solid #d97706',
                fontSize: '0.78rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
              }}>
                <span>
                  Couldn't load clock status{punchUiState.statusCode ? ` (${punchUiState.statusCode})` : ''}. You can still punch in.
                </span>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['timeclock-status'] })}
                  style={{ background: 'none', border: '1px solid currentColor', borderRadius: 4, padding: '0.1rem 0.4rem', color: 'inherit', cursor: 'pointer', fontWeight: 600 }}
                >
                  Retry
                </button>
              </div>
            )}
            {/* Status */}
            {punchUiState.kind === 'ready' && (
              <div style={{
                padding: '0.75rem', borderRadius: 'var(--radius-sm)', marginBottom: '1rem',
                background: punchUiState.punchedIn ? '#f0fdf4' : 'var(--bg)',
                border: `1px solid ${punchUiState.punchedIn ? '#bbf7d0' : 'var(--border)'}`,
              }}>
                <div style={{
                  fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem',
                  color: punchUiState.punchedIn ? '#16a34a' : 'var(--text-muted)',
                }}>
                  {punchUiState.punchedIn ? 'Currently clocked in' : 'Not clocked in'}
                </div>
                {punchUiState.punchedIn && elapsed && (
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>
                    {elapsed}
                  </div>
                )}
              </div>
            )}

            {/* Action button */}
            {punchUiState.kind === 'ready' && punchUiState.punchedIn ? (
              <button
                onClick={() => punchOut.mutate()}
                disabled={punchOut.isPending}
                style={{
                  width: '100%', padding: '0.75rem', borderRadius: 'var(--radius-md)',
                  border: 'none', background: '#dc2626', color: '#fff',
                  fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  opacity: punchOut.isPending ? 0.7 : 1,
                }}
              >
                <LogOut size={18} />
                {punchOut.isPending ? 'Punching out...' : 'Punch Out'}
              </button>
            ) : (
              <button
                onClick={() => punchIn.mutate()}
                disabled={punchIn.isPending}
                style={{
                  width: '100%', padding: '0.75rem', borderRadius: 'var(--radius-md)',
                  border: 'none', background: '#16a34a', color: '#fff',
                  fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                  opacity: punchIn.isPending ? 0.7 : 1,
                }}
              >
                <LogIn size={18} />
                {punchIn.isPending ? 'Punching in...' : 'Punch In'}
              </button>
            )}
          </div>
        )}

        {/* Feedback */}
        {feedback && (
          <div style={{
            marginTop: '0.75rem', padding: '0.5rem', borderRadius: 'var(--radius-sm)',
            textAlign: 'center', fontSize: '0.85rem', fontWeight: 500,
            background: feedback.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color: feedback.type === 'success' ? '#16a34a' : '#dc2626',
          }}>
            {feedback.msg}
          </div>
        )}
    </Dialog>
  );
}
